import type { SupportedLanguages } from '../../i18n/constants';

export enum SupportedCountryCodeEnum {
  US = 'US',
  DE = 'DE',
  CA = 'CA',
  AT = 'AT',
  NZ = 'NZ',
}

/**
 * Countries where the region is derived from city selection rather than picked independently.
 * For these countries the region selector is hidden and selecting a city auto-sets the region.
 */
export const CITY_DERIVED_REGION_COUNTRIES: ReadonlySet<SupportedCountryCodeEnum> = new Set([
  SupportedCountryCodeEnum.NZ,
]);

export const CITY_SEARCH_LIMIT = 100;

export type Country = {
  code: SupportedCountryCodeEnum | '';
  name: string;
  isActive: boolean;
  postalCodeName: string;
  subdivisionName: string;
  cookieConsentEnabled: boolean;
  usercentricsSettingsId: string;
  defaultLanguage: SupportedLanguages;
};

export type CookieConsent = {
  enabled: boolean;
};

/**
 * Each country has a "fallback" territory with NULL code and name specified for it. That allows us to describe users
 * from known country but yet unknown region. However, the players do not need to know about such "placeholder" territories,
 * so without certain parameters the API would return only the records matching Territory<false> (with code and name present).
 */
export type Territory<IncludeCountryFallback extends boolean = false> = {
  key: string;
  countryCode: string;
  country: Country;
  isActive: boolean;
  isScModeAvailable: boolean;
  cookieConsentEnabled: boolean;
  usercentricsSettingsId: string;
} & (IncludeCountryFallback extends true
  ?
      | {
          code: string;
          name: string;
        }
      | {
          code: null;
          name: null;
        }
  : {
      code: string;
      name: string;
    });

export type City = {
  name: string;
  stateCode: string;
};

export type MappedData<T> = {
  list: T[];
  map: Partial<Record<string, T>>;
};

export type RawGeoData = {
  ip: string;
  country: string;
  countryCode: string;
  region: string;
  city: string;
  isVPN: boolean;
  ipVersion: number;
  geoVersion: number;
};

export type CleanGeoData = {
  territoryKey: string;
  territoryLegalAge: number;
  countryCode: SupportedCountryCodeEnum;
  countryName: string;
  cookieConsentEnabled: string;
  usercentricsSettingsId: string;
  defaultLanguage: SupportedLanguages;
} & ({ territoryName: string; territoryCode: string } | { territoryName: null; territoryCode: null });

export type PhoneNumberCountryCodeType = {
  countryName: string;
  phoneCode: string;
};
